import React from 'react';
import {Map, List} from 'immutable';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {Atom, Plugins} from '@packages/systems/wfdl';
import SharedConfig, {
  CommerceCountry,
} from '@packages/systems/core/constants/SharedConfig';
import {checkoutInputStyle, mobileCheckoutInputStyle} from './styles';
import {px} from '@packages/systems/core/utils/WebflowCssSubset';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';

const {commerceCountryList} = SharedConfig;

// get list of all countries and sort it by name of country
const sortedCountries = [...commerceCountryList].sort((a, b) =>
  a.name.localeCompare(b.name)
);

const CountrySelector = Atom.createRender(
  ({attributes, classes, context, generateAttributes}) => {
    // the list of countries eventually used by the react component
    let inputCountryOptions: Array<never> | Array<CommerceCountry | any> = [];

    // the default country, if false no default provided
    let defaultInputValue = false;

    // convert immutable list of shipping zones to array of objects
    const ecommShippingZone = (
      context.get('ecommShippingZones') || List()
    ).toJS();

    // Get the current value of the country set when adding a biz address
    const businessAddressCountry = context.getIn([
      'ecommBusinessAddress',
      'country',
    ]);

    // determine if restOfTheWorld is ever true in the shipping zone list
    const restOfWorldUsed = ecommShippingZone.some((item: any) => {
      return item.isRestOfTheWorld === true;
    });

    if (restOfWorldUsed || ecommShippingZone.length === 0) {
      // restOfWorldUsed in shipping zones, so show all countries in list
      inputCountryOptions = sortedCountries; // input options just list of all countries
      if (businessAddressCountry) {
        // default to country of biz address
        defaultInputValue = businessAddressCountry;
      }
    } else {
      // restOfWorldUsed not used, so must reduce countries only to ones in in shipping zones
      const onlyShippingZonesInputCountryOptions: Array<any> = [];

      // create an array containing only the countries from the shipping zone list
      ecommShippingZone.forEach((item: any) => {
        if (item.countries) {
          onlyShippingZonesInputCountryOptions.push(...item.countries);
        }
      });

      // Use only the countries in the input that are in the shipping zones
      inputCountryOptions = sortedCountries.filter((item) => {
        // filter list down to only countries found in shipping zones
        return onlyShippingZonesInputCountryOptions.includes(item.code);
      });

      // now set default if it...
      if (
        onlyShippingZonesInputCountryOptions.includes(businessAddressCountry)
      ) {
        // if the business country is found in the shipping zones, the input then defaults to that biz country
        defaultInputValue = businessAddressCountry;
      } else if (
        onlyShippingZonesInputCountryOptions.includes('US') ||
        businessAddressCountry === 'US'
      ) {
        // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'boolean'.
        defaultInputValue = 'US';
      } // otherwise just select the first option in HTML select, i.e. default behavior
    }

    return (
      <select
        {...generateAttributes(attributes, classes)}
        name="address_country"
        // @ts-expect-error - TS2322 - Type 'true | ""' is not assignable to type 'string | number | readonly string[] | undefined'.
        defaultValue={defaultInputValue ? defaultInputValue : ''}
      >
        {inputCountryOptions.map(({code, name}) => (
          <option value={code} key={code}>
            {name}
          </option>
        ))}
      </select>
    );
  }
);

const staticBindings = Map({
  value: List([
    Map({
      slug: 'shippingAddressCountry',
      type: 'PlainText',
    }),
  ]),
});

const config = createIntegratedAtom({
  displayName: 'Country',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Form'), iconName('Select')),
  },
  showEmpty: false,
  render: CountrySelector,
  style: {
    base: {
      main: {
        ...checkoutInputStyle,
        noPseudo: {
          ...checkoutInputStyle.noPseudo,
          marginBottom: px(0),
        },
      },
      tiny: mobileCheckoutInputStyle,
    },
  },
  staticBindings,
  fields: {},
});

export default config;
